// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: episodes/$episodeId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Episodes catalog for the Danger Dan! cartoon series. Admin-managed, publicly readable when published.
 */

export interface EpisodesRequest {
  title: string;
  description: string;
  thumbnailUrl: string;
  videoUrl: string;
  season: number | TimeOperation | IncrementOperation | TokenAmount;
  episodeNumber: number | TimeOperation | IncrementOperation | TokenAmount;
  published: boolean;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface EpisodesResponse {
  title: string;
  description: string;
  thumbnailUrl: string;
  videoUrl: string;
  season: number;
  episodeNumber: number;
  published: boolean;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Episodes operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildEpisodes(episodeId: string, data: EpisodesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `episodes/${episodeId}`, document: doc };
}

/**
 * Admin or founder only. Creates a new episode with title, description, thumbnailUrl, videoUrl (embed link), season number, episodeNumber, published flag (set to false for drafts), and createdAt timestamp (Unix seconds). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setEpisodes(episodeId: string, data: EpisodesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`episodes/${episodeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Episodes: ${formatError(error)}`);
    return false;
  }
}

export type EpisodesRequestUpdate = Partial<EpisodesRequest>;

/**
 * Build a Episodes update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateEpisodes(episodeId: string, data: EpisodesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `episodes/${episodeId}`, document: doc };
}

/**
 * Admin or founder only. Can update any field including toggling published to make an episode visible or hide it, changing description, thumbnailUrl, videoUrl, season, episodeNumber, or title. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateEpisodes(episodeId: string, data: EpisodesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`episodes/${episodeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Episodes: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read published episodes (published == true). Admin and founder can read all episodes regardless of published status, including drafts.
   (Get Single Item)
 */
export async function getEpisodes(episodeId: string): Promise<EpisodesResponse | null> {
  try {
    const result = await get(`episodes/${episodeId}`);
    return result as EpisodesResponse | null;
  } catch (error) {
    console.error(`Error getting Episodes: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Episodes document. (
  Read Operation Details: Anyone can read published episodes (published == true). Admin and founder can read all episodes regardless of published status, including drafts.
  )
 */
export function subscribeEpisodes(callback: (data: EpisodesResponse | null) => void, episodeId: string): Promise<() => Promise<void>> {
  return subscribe(`episodes/${episodeId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as EpisodesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Episodes at path '${`episodes/${episodeId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Episodes items from collection episodes
 
  Read Operation Details: Anyone can read published episodes (published == true). Admin and founder can read all episodes regardless of published status, including drafts.
  
 */
export async function getManyEpisodes(filter: string = ""): Promise<EpisodesResponse[]> {
  try {
    const results = await get("episodes", { prompt: filter });
    return Array.isArray(results) ? results as EpisodesResponse[] : [];
  } catch (error) {
    console.error(`Error getting Episodes collection at path 'episodes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Episodes collection at episodes
 
  Read Operation Details: Anyone can read published episodes (published == true). Admin and founder can read all episodes regardless of published status, including drafts.
  
 */
export function subscribeManyEpisodes(callback: (data: EpisodesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("episodes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as EpisodesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Episodes collection at path '${"episodes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Episodes items from collection episodes
 
  Read Operation Details: Anyone can read published episodes (published == true). Admin and founder can read all episodes regardless of published status, including drafts.
  
 */
export async function getAllEpisodes(filter: string = ""): Promise<EpisodesResponse[]> {
  try {
    const results = await get("episodes", { prompt: filter });
    return Array.isArray(results) ? results as EpisodesResponse[] : [];
  } catch (error) {
    console.error(`Error getting Episodes collection at path 'episodes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Episodes collection at episodes
 
  Read Operation Details: Anyone can read published episodes (published == true). Admin and founder can read all episodes regardless of published status, including drafts.
  
 */
export function subscribeAllEpisodes(callback: (data: EpisodesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("episodes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as EpisodesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Episodes collection at path '${"episodes"}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can remove an episode from the catalog.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteEpisodes(episodeId: string): Promise<boolean> {
  try {
    const succeeded = await set(`episodes/${episodeId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Episodes: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Episodes for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteEpisodes(episodeId: string): DocumentOperation {
  return { path: `episodes/${episodeId}`, document: null };
}

